import { IconButton, InputAdornment } from '@mui/material';
import { Clear, Search } from '@mui/icons-material';
import React from 'react';
import { useField } from 'formik';
import { BaseTextField, BaseTextFieldProps } from '../BaseTextField';

export type SearchBoxRawProps = BaseTextFieldProps & {
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onClear?: () => void;
  clearable?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  className?: string;
  ariaLabel?: string;
};

export const SearchBoxRaw = React.forwardRef<HTMLDivElement, SearchBoxRawProps>(({ clearable, value, onChange, onClear, ariaLabel, slotProps, ...rest }, ref) => {
  const handleClear = onClear ?? (() => onChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>));

  return (
    <BaseTextField
      {...rest}
      value={value}
      onChange={onChange}
      ref={ref}
      slotProps={{
        ...slotProps,
        htmlInput: {
          ...slotProps?.htmlInput,
          'aria-label': ariaLabel,
        },
        input: {
          ...slotProps?.input,
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: clearable && value && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear}>
                <Clear fontSize="small" color="action" />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
});

export type SearchBoxProps = Omit<SearchBoxRawProps, 'value' | 'onChange' | 'onClear'> & {
  name: string;
};

export const SearchBox = React.forwardRef<HTMLDivElement, SearchBoxProps>(({ name, ...rest }, ref) => {
  const [ field, _meta, helpers ] = useField({ name, type: 'text' });

  return (
    <SearchBoxRaw
      {...rest}
      name={name}
      value={field.value}
      onChange={field.onChange}
      onClear={() => helpers.setValue('')}
      ref={ref}
    />
  );
});
